import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { History, Loader2, Pencil, TrendingUp, CalendarClock } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import api from '../../lib/api';
import { formatImporte } from '../../lib/utils';

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const mesLabel = (p) => { const [y, m] = p.split('-'); return `${MESES[+m - 1]} ${y.slice(2)}`; };

const Leyenda = () => (
  <div className="flex flex-wrap gap-4 text-xs text-slate-600">
    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-sky-100 border border-sky-300" /> Mes de actualización según contrato</span>
    <span className="inline-flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5 text-emerald-600" /> Se actualizó de verdad (subió)</span>
    <span className="inline-flex items-center gap-1"><Pencil className="w-3 h-3 text-amber-600" /> Monto modificado a mano</span>
  </div>
);

const Celda = ({ m }) => {
  if (!m) return <td className="px-2 py-1.5 text-center text-slate-300">—</td>;
  return (
    <td className={`px-2 py-1.5 text-right font-mono text-sm whitespace-nowrap ${m.ajuste_contrato ? 'bg-sky-50' : ''}`}>
      <span className="inline-flex items-center gap-1 justify-end">
        {m.aumento_real && <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />}
        {m.manual && <Pencil className="w-3 h-3 text-amber-600" />}
        {m.neto ? `$ ${formatImporte(m.neto, 2)}` : '—'}
      </span>
    </td>
  );
};

export const HistorialNetoButton = ({ edificioId, periodo }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get(`/facturacion/${edificioId}/historial-neto`, { params: { hasta: periodo, meses: 6 } })
      .then(r => setData(r.data)).catch(() => { toast.error('No se pudo cargar el historial'); setOpen(false); });
  }, [open, edificioId, periodo]);

  return (
    <>
      <Button variant="outline" size="sm" className="h-8 ml-2" onClick={() => setOpen(true)} data-testid="historial-neto-btn">
        <History className="w-4 h-4 mr-1.5" /> Historial neto
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[96vw] xl:max-w-7xl" data-testid="historial-neto-dialog">
          <DialogHeader>
            <DialogTitle>Historial de monto neto · últimos 6 meses</DialogTitle>
            <DialogDescription>Neto facturado por unidad. Compará el calendario del contrato con los aumentos reales.</DialogDescription>
          </DialogHeader>
          <Leyenda />
          {!data ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-10 text-slate-400" /> : (
            <div className="overflow-auto max-h-[60vh] rounded-lg border border-slate-200">
              <table className="w-full text-sm" data-testid="historial-neto-tabla">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr>
                    <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 px-3 py-2">Unidad</th>
                    <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 px-2 py-2">Inquilino</th>
                    <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 px-2 py-2"><CalendarClock className="w-3.5 h-3.5 inline mr-1" />Contrato</th>
                    {data.periodos.map(p => <th key={p} className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 px-2 py-2">{mesLabel(p)}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.filas.map((f, i) => (
                    <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`historial-fila-${f.unidad_id}`}>
                      <td className="px-3 py-1.5 font-medium">{f.unidad_nombre}</td>
                      <td className="px-2 py-1.5 max-w-[180px] truncate" title={f.inquilino}>{f.inquilino}</td>
                      <td className="px-2 py-1.5 text-xs text-slate-500 whitespace-nowrap">{f.frecuencia || (f.es_cowork ? 'Cowork' : '-')}</td>
                      {data.periodos.map(p => <Celda key={p} m={f.meses[p]} />)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
